/**
 * cart-addon-guard.js
 * Guardian de los cargos adicionales (mascota extra / humano, etc.).
 *
 * Problema: custom-product-options.js agrega el cargo como una SEGUNDA
 * linea del carrito ("Mascota extra - Personalizacion"). Si esa linea
 * falla al agregarse o la clienta la borra, el pedido llega sin el cargo
 * (ej. pedido #P1503).
 *
 * Que hace:
 *  1. En producto: guarda en el producto principal una propiedad oculta
 *     "_recargo" con el monto del cargo (ej. 750).
 *  2. Revisa el carrito: cada producto con "_recargo" debe tener su linea
 *     de cargo (mismo grupo y cantidad). Si falta la agrega; si sobra
 *     (se borro el principal) la quita.
 *  3. Revisa al cargar cada pagina, tras cambios en el carrito y antes
 *     de ir al checkout.
 *  4. Oculta "eliminar" y "cantidad" en las lineas de cargo.
 */
(function () {
  'use strict';

  var ADDON_HANDLE = 'cargo-adicional-personalizacion';
  var GROUP_KEY = '_grupo_personalizacion';
  var SURCHARGE_KEY = '_recargo';

  var nativeFetch = window.fetch.bind(window);
  var addonProductPromise = null;
  var reconciling = null;

  function urlPath(input) {
    try {
      var raw = typeof input === 'string' ? input : input && input.url;
      return new URL(raw, window.location.origin).pathname;
    } catch (e) {
      return '';
    }
  }

  function isCartAdd(path) {
    return /\/cart\/add(\.js)?$/.test(path);
  }

  function isCartMutation(path) {
    // No incluye /cart/add: la pagina de producto agrega el cargo justo
    // despues y redirige al carrito, donde se revisa al cargar.
    return /\/cart\/(change|update|clear)(\.js)?$/.test(path);
  }

  function currentSurcharge() {
    var wrappers = document.querySelectorAll('[data-custom-options]');
    for (var i = 0; i < wrappers.length; i++) {
      var v = parseFloat(wrappers[i].getAttribute('data-current-surcharge') || '0') || 0;
      if (v > 0) return v;
    }
    return 0;
  }

  function tagMainItem(body) {
    if (!(body instanceof FormData)) return;
    if (!body.has('properties[' + GROUP_KEY + ']')) return;
    if (body.has('properties[' + SURCHARGE_KEY + ']')) return;
    var surcharge = currentSurcharge();
    if (surcharge > 0) {
      body.append('properties[' + SURCHARGE_KEY + ']', String(surcharge));
    }
  }

  function getAddonProduct() {
    if (!addonProductPromise) {
      addonProductPromise = nativeFetch('/products/' + ADDON_HANDLE + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) {
          if (!r.ok) throw new Error('No se encontro el producto de cargo adicional');
          return r.json();
        })
        .catch(function (err) {
          addonProductPromise = null;
          throw err;
        });
    }
    return addonProductPromise;
  }

  function variantForAmount(product, amount) {
    var cents = Math.round(amount * 100);
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].price === cents && product.variants[i].available !== false) {
        return product.variants[i];
      }
    }
    return null;
  }

  function isAddonItem(item) {
    return item.handle === ADDON_HANDLE;
  }

  function getProp(item, key) {
    return item.properties ? item.properties[key] : undefined;
  }

  function postJson(url, payload) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  async function reconcileCart() {
    if (reconciling) return reconciling;
    reconciling = (async function () {
      var cart = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        return r.json();
      });

      var mains = {};
      var addons = {};
      cart.items.forEach(function (item) {
        var group = getProp(item, GROUP_KEY);
        if (!group) return;
        if (isAddonItem(item)) {
          addons[group] = item;
        } else {
          var surcharge = parseFloat(getProp(item, SURCHARGE_KEY) || '0') || 0;
          if (surcharge > 0) mains[group] = { item: item, surcharge: surcharge };
        }
      });

      var changed = false;
      var product = null;

      for (var group in mains) {
        if (!Object.prototype.hasOwnProperty.call(mains, group)) continue;
        var main = mains[group];
        var addon = addons[group];
        if (!product) product = await getAddonProduct();
        var variant = variantForAmount(product, main.surcharge);
        if (!variant) {
          console.error('[cart-addon-guard] No hay variante de cargo para el monto: ' + main.surcharge);
          continue;
        }

        if (addon && addon.variant_id !== variant.id) {
          await postJson('/cart/change.js', { id: addon.key, quantity: 0 });
          addon = null;
          changed = true;
        }

        if (!addon) {
          var res = await postJson('/cart/add.js', {
            items: [
              {
                id: variant.id,
                quantity: main.item.quantity,
                properties: {
                  _grupo_personalizacion: group,
                  _para_producto: main.item.product_title
                }
              }
            ]
          });
          if (!res.ok) throw new Error('No se pudo agregar el cargo adicional');
          changed = true;
        } else if (addon.quantity !== main.item.quantity) {
          await postJson('/cart/change.js', { id: addon.key, quantity: main.item.quantity });
          changed = true;
        }
      }

      var mainGroups = {};
      cart.items.forEach(function (item) {
        var g = getProp(item, GROUP_KEY);
        if (g && !isAddonItem(item)) mainGroups[g] = true;
      });
      for (var g in addons) {
        if (!Object.prototype.hasOwnProperty.call(addons, g)) continue;
        if (!mainGroups[g]) {
          await postJson('/cart/change.js', { id: addons[g].key, quantity: 0 });
          changed = true;
        }
      }

      return changed;
    })();

    try {
      return await reconciling;
    } finally {
      reconciling = null;
    }
  }

  function reloadIfChanged(changed) {
    if (!changed) return;
    var onCart = /^\/cart\/?$/.test(window.location.pathname);
    var drawerOpen = document.querySelector('cart-drawer.active, cart-drawer[open]');
    if (onCart || drawerOpen) window.location.reload();
  }

  window.fetch = function (input, init) {
    var path = urlPath(input);
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

    if (method === 'POST' && isCartAdd(path) && init && init.body) {
      tagMainItem(init.body);
    }

    var promise = nativeFetch(input, init);

    if (method === 'POST' && isCartMutation(path)) {
      promise.then(function () {
        setTimeout(function () {
          reconcileCart().then(reloadIfChanged).catch(function (err) {
            console.error('[cart-addon-guard]', err);
          });
        }, 300);
      });
    }
    return promise;
  };

  document.addEventListener(
    'submit',
    function (evt) {
      var submitter = evt.submitter;
      if (!(submitter && submitter.name === 'checkout')) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      submitter.setAttribute('aria-busy', 'true');
      reconcileCart()
        .catch(function (err) {
          console.error('[cart-addon-guard]', err);
        })
        .then(function () {
          window.location.href = '/checkout';
        });
    },
    true
  );

  document.addEventListener(
    'click',
    function (evt) {
      var link = evt.target.closest && evt.target.closest('a[href$="/checkout"]');
      if (!link) return;
      evt.preventDefault();
      reconcileCart()
        .catch(function (err) {
          console.error('[cart-addon-guard]', err);
        })
        .then(function () {
          window.location.href = link.href;
        });
    },
    true
  );

  function lockAddonRows() {
    var links = document.querySelectorAll('a[href*="/products/' + ADDON_HANDLE + '"]');
    links.forEach(function (link) {
      var row = link.closest('.cart-item, tr, li');
      if (!row || row.dataset.addonLocked === 'true') return;
      row.dataset.addonLocked = 'true';
      row
        .querySelectorAll('cart-remove-button, .cart-remove-button, quantity-input, .quantity, .cart-item__quantity-wrapper')
        .forEach(function (el) {
          el.style.display = 'none';
        });
      link.removeAttribute('href');
      link.style.pointerEvents = 'none';
    });
  }

  var style = document.createElement('style');
  style.textContent =
    '[data-addon-locked="true"] cart-remove-button, [data-addon-locked="true"] quantity-input { display: none !important; }';
  document.head.appendChild(style);

  function init() {
    lockAddonRows();
    new MutationObserver(lockAddonRows).observe(document.body, { childList: true, subtree: true });
    reconcileCart().then(reloadIfChanged).catch(function (err) {
      console.error('[cart-addon-guard]', err);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
